import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddInputs from "../../../../components/Add/AddInputs"
import S3ImageUpload from "../../../../components/S3uploads/S3ImageUpload";
import useApi from "../../../../services/useApi";
import { setCorporateDetails } from "../../../../store/slices/corporateDetailsSlice";
import { setExpandedCorporateDetails, updateExpandedCorporateDetails } from "../../../../store/slices/expandedCorporateDetailsSlice";
import { RootState } from "../../../../store";
import { showToast } from "../../../../components/toastMessage/toastSlice";
import config from "../../../../config/config";
import Image from "../../../../components/images/Image";
import { setAddLoading } from "../addMDSlice";
import { useNavigate } from "react-router";
import { clearSignaturePath } from "../../../../store/slices/signatureSlice";
import CustomersTermsAndConditions from "../../../../components/CustomModal/CustomersTermsAndConditions";
import usePrivilege from "../../../../components/CustomHooks/usePrivilege";
import { convertDate, formatDate } from "../../../../components/commonfunctions/convertDate";

interface Customer {
  id: string;
  company_code: string;
  company_name: string;
  contact_person_name: string;
  representative_profession: string;
  contact_email: string;
  company_license_number: string;
  contact_phone_number: string;
  business_type: string;
  country: string;
  province: string;
  region: string;
  created_at: string;
  company_license_expiry_date: string;
  company_license_front: string;
  company_license_back: string;
}

export interface corporateDetailsRef {
  handleSubmit: () => void;
}

interface CorporateDetailsProps {
  id?: string;
  type?: string;
  handleNext?: () => void;
}

interface CorporateDetailsState {
  company_id: string;
  company_name: string;
  employee_id_number: string;
  employee_id_card_front_page: string;
  point_of_contact_person_name: string;
  point_of_contact_person_profession: string;
  point_of_contact_person_email: string;
  company_license_front_page: string;
  company_license_back_page: string;
  company_license_number: string;
  company_license_expiry_date: string;
}

const CorporateDetails = forwardRef<corporateDetailsRef, CorporateDetailsProps>(
  ({ id, type, handleNext }, ref) => {
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const { getAPI, PatchAPI } = useApi();
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Record<string, string>>({});
    const [termsAndConditions, setTermsAndConditions] = useState(false);
    const [acceptButtonLoading, setAcceptButtonLoading] = useState(false);
    const hasCompanyPrivilege = usePrivilege("CMGT4") || usePrivilege("CMGT3") || usePrivilege("CMGT2");
    const corporateDetails = useSelector(
      (state: RootState) => state.corporateDetails
    );
    const { user_id, profile } = useSelector((state: RootState) => state.addMD);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [searchedCompany, setSearchedCompany] = useState('');

    const [formData, setFormData] = useState<CorporateDetailsState>({
      company_id: corporateDetails.company_id || "",
      company_name: "",
      employee_id_number: corporateDetails.employee_id_number || "",
      employee_id_card_front_page:
        corporateDetails.employee_id_card_front_page || "",
      point_of_contact_person_name: "",
      point_of_contact_person_profession: "",
      point_of_contact_person_email: "",
      company_license_front_page:
        "",
      company_license_back_page: "",
      company_license_number: "",
      company_license_expiry_date: "",
    });

    const handleSubmit = async () => {
      try {
        dispatch(setAddLoading(true));
        setLoading(true);
        setError({}); // Clear previous errors

        // Validate required fields
        const validationErrors: Record<string, string> = {};

        if (!formData.company_name) {
          validationErrors.company_name = "This field is required";
        }
        if (!formData.employee_id_number) {
          validationErrors.employee_id_number = "This field is required";
        }
        if (!formData.employee_id_card_front_page) {
          validationErrors.employee_id_card_front_page =
            "This field is required";
        }

        // If there are validation errors, set them and return
        if (Object.keys(validationErrors).length > 0) {
          setError(validationErrors);
          return;
        }

        const payload = {
          corporate_details: {
            company_id: formData.company_id,
            employee_id_card_number: formData.employee_id_number,
            employee_id_card_front_page: formData.employee_id_card_front_page,
          },
        };

        const response = await PatchAPI(`customers/${user_id}`, payload);

        if (!response.error) {
          dispatch(
            setCorporateDetails({
              company_id: formData.company_id,
              employee_id_number: formData.employee_id_number,
              employee_id_card_front_page: formData.employee_id_card_front_page,
            })
          );
          if( profile === 'Pending' || profile === ''){
            setTermsAndConditions(true);
          } else {
            navigate(`/user-management/${type
              ?.toLowerCase()
              .replace(/ /g, "-")
              .slice(0, -1)}-management`);
            
              dispatch(showToast({ message: "Details updated successfully", type: "success" }));

          }
        } else {
          setError({
            general:
              response.data?.response?.data?.message ||
              "Failed to update corporate details",
          });
        }
      } catch (err) {
        console.error("Error updating corporate details:", err);
        setError({
          general: "An error occurred while updating corporate details",
        });
      } finally {
        setLoading(false);
        dispatch(setAddLoading(false));
      }
    };

    const handleTermsAndConditions = async (signature: string) => {
      setAcceptButtonLoading(true);
      let payload = {
        signature_photo: signature,
      };

      let res = await PatchAPI(
        `${type?.toLowerCase()?.split(" ").join("-")}/${user_id}`,
        payload
      );
      if (!res.error) {
        setTermsAndConditions(false);
        navigate(`/user-management/${type
          ?.toLowerCase()
          .replace(/ /g, "-")
          .slice(0, -1)}-management`);
        dispatch(showToast({ message: "Details added successfully", type: "success" }));
        dispatch(clearSignaturePath());
      } else {
        dispatch(showToast({ message: res.data?.response?.data?.message || "An error occurred", type: "error" }));
      }
      setAcceptButtonLoading(false);
    };

    useImperativeHandle(ref, () => ({
      handleSubmit: handleSubmit,
    }));

    const fetchCustomers = async (searchTerm: string = '', pageNumbeChange?: boolean) => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        const prevSearchTerm = searchedCompany;
        const prevPage = currentPage;
        let pageToFetch = currentPage;

        if (searchTerm !== prevSearchTerm) {
          pageToFetch = 1;
          setCurrentPage(1);
        } else if (pageNumbeChange && prevSearchTerm === searchTerm && prevPage === currentPage) {
          pageToFetch = currentPage + 1;
          setCurrentPage(pageToFetch);
        }

        const response = await getAPI(`companies/?page_number=${pageToFetch}`);

        if (response.data) {
          const newCompanies = response.data.data || [];
          setCustomers((prevCompanies) => {
            const uniqueCompanies = newCompanies.filter((newCompany: Customer) =>
              !prevCompanies.some((prevCompany) => prevCompany.id === newCompany.id)
            );
            return [...prevCompanies, ...uniqueCompanies];
          });
        }
      } catch (err) {
        console.error("Error fetching customers:", err);
        setError((prev) => ({
          ...prev,
          company_name: "Failed to fetch companies",
        }));
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      const initializeFormData = async () => {
        if (corporateDetails.company_id) {
          try {
            const response = await getAPI(`companies/${corporateDetails.company_id}`);
            if (response.data) {
              const companyData = response.data.data;
              const newFormData = {
                company_id: companyData.id,
                company_name: companyData.company_name,
                point_of_contact_person_name: companyData.contact_person_name,
                point_of_contact_person_profession: companyData.representative_profession,
                point_of_contact_person_email: companyData.contact_email,
                company_license_number: companyData.company_license_number,
                company_license_expiry_date: companyData.company_license_expiry_date,
                company_license_front_page: companyData.company_license_front,
                company_license_back_page: companyData.company_license_back,
              };
              
              setFormData(prev => ({
                ...prev,
                ...newFormData
              }));

              // Update expanded corporate details in Redux
              dispatch(setExpandedCorporateDetails({
                company_id: companyData.id,
                company_name: companyData.company_name,
                business_type: companyData.business_type || '',
                contact_email: companyData.contact_email,
                contact_person_name: companyData.contact_person_name,
                company_license_back: companyData.company_license_back,
                company_license_front: companyData.company_license_front,
                company_license_number: companyData.company_license_number,
                employee_id_card_number: formData.employee_id_number,
                representative_profession: companyData.representative_profession,
                company_license_expiry_date: convertDate(companyData.company_license_expiry_date),
                employee_id_card_front_page: formData.employee_id_card_front_page,
              }));
            }
          } catch (err) {
            console.error("Error fetching company details:", err);
          }
        }
      };

      initializeFormData();
      fetchCustomers();
    }, []);

    const handleInputChanges = (id: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));

      // Clear error for this field
      if (error[id]) {
        setError((prev) => {
          const newErrors = { ...prev };
          delete newErrors[id];
          return newErrors;
        });
      }

      if (id === "company_name") {
        const selectedCustomer = customers.find(
          (customer) => customer.company_name === value
        );
        if (selectedCustomer) {
          const updatedFormData = {
            company_id: selectedCustomer.id,
            point_of_contact_person_name: selectedCustomer.contact_person_name,
            point_of_contact_person_profession: selectedCustomer.representative_profession,
            point_of_contact_person_email: selectedCustomer.contact_email,
            company_license_number: selectedCustomer.company_license_number,
            company_license_expiry_date: selectedCustomer.company_license_expiry_date,
            company_license_front_page: selectedCustomer.company_license_front,
            company_license_back_page: selectedCustomer.company_license_back,
          };

          setFormData((prev) => ({
            ...prev,
            ...updatedFormData
          }));

          // Update expanded corporate details in Redux
          dispatch(updateExpandedCorporateDetails({
            company_id: selectedCustomer.id,
            company_name: selectedCustomer.company_name,
            business_type: selectedCustomer.business_type || '',
            contact_email: selectedCustomer.contact_email,
            contact_person_name: selectedCustomer.contact_person_name,
            company_license_back: selectedCustomer.company_license_back,
            company_license_front: selectedCustomer.company_license_front,
            company_license_number: selectedCustomer.company_license_number,
            representative_profession: selectedCustomer.representative_profession,
            company_license_expiry_date: selectedCustomer.company_license_expiry_date,
          }));
        }
      } else {
        // Update individual field in expanded corporate details
        const fieldMapping: Record<string, string> = {
          employee_id_number: 'employee_id_card_number',
          employee_id_card_front_page: 'employee_id_card_front_page',
        };

        if (fieldMapping[id]) {
          dispatch(updateExpandedCorporateDetails({
            [fieldMapping[id]]: value
          }));
        }
      }
    };

    const handleFileUpload = (upload_id: string, file: string) => {
      if (upload_id === "employee_id_card_front_page") {
        setFormData((prev) => ({
          ...prev,
          employee_id_card_front_page: file,
        }));

        // Update expanded corporate details in Redux
        dispatch(updateExpandedCorporateDetails({
          employee_id_card_front_page: file
        }));

        // Clear error for this field
        if (error.employee_id_card_front_page) {
          setError((prev) => {
            const newErrors = { ...prev };
            delete newErrors.employee_id_card_front_page;
            return newErrors;
          });
        }
      }
    };

    const isImageUrl = (url: string): boolean => {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    };

    const inputs = {
      "": {
        "Company Name": {
          required: true,
          disabled: false,
          id: "company_name",
          type: "dropDown",
          options: customers.map((customer) => customer.company_name),
          noSearch: true,
          tabIndex: 0,
          loading: isLoading,
        },
        "Point of Contact Person's Name": {
          required: false,
          disabled: true,
          id: "point_of_contact_person_name",
          type: "input",
          maxLength: 50,
          tabIndex: 1,
        },
        "Point of Contact Person's Email": {
          required: false,
          disabled: true,
          id: "point_of_contact_person_email",
          type: "input",
          maxLength: 100,
          tabIndex: 2,
        },
        "Point of Contact Person's Profession": {
          required: false,
          disabled: true,
          id: "point_of_contact_person_profession",
          type: "input",
          tabIndex: 3,
          disabledDate: true,
        },
        "Company License Number": {
          required: false,
          disabled: true,
          id: "company_license_number",
          type: "input",
          tabIndex: 5,
          maxLength: 80,
        },
        "Company License Expiry Date": {
          required: false,
          disabled: true,
          id: "company_license_expiry_date",
          type: "date",
          tabIndex: 4,
        },
        "Employee ID Number": {
          id: "employee_id_number",
          type: "input",
          required: true,
          disabled: false,
          tabIndex: 6,
        },
      },
    };

    return (
      <div data-testid="corporate-details">
        <AddInputs
          mapping={true}
          inputs={inputs}
          totalInputCount={7}
          onInputChange={handleInputChanges}
          inputValues={{
            company_name: formData.company_name,
            employee_id_number: formData.employee_id_number,
            point_of_contact_person_name: formData.point_of_contact_person_name,
            point_of_contact_person_email:
              formData.point_of_contact_person_email,
            point_of_contact_person_profession:
              formData.point_of_contact_person_profession,
            company_license_number: formData.company_license_number,
            company_license_expiry_date: formData.company_license_expiry_date,
          }}
          errors={error}
          edit={false}
          loadMoreOptions={(searchTerm: string, pageNumbeChange?: boolean) => fetchCustomers(searchTerm, pageNumbeChange)}
        />
        <div className="flex gap-10">
          <div className="mt-4 mb-4 w-1/4">
            <h1 className="text-sm font-semibold text-black">
              Company License Front Page
            </h1>
            {formData.company_license_front_page ? (
              <div className="w-full flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1 ">
                <p className="more-lines">
                  {formData.company_license_front_page.split("/").pop()}
                </p>
                {hasCompanyPrivilege && <Image
                  src="file-eye"
                  className="cursor-pointer "
                  objectFit={"contain"}
                  onClick={() => {
                    const fileUrl =
                      config?.cdnURL +
                      "public/" +
                      formData.company_license_front_page;
                    const link = document.createElement("a");
                    link.href = fileUrl;
                    link.target = "_blank";
                    link.rel = "noopener noreferrer";
                    link.click();
                  }}
                />}
              </div>
            ) : (
              <p className="mt-1 font-medium text-gray-900">-</p>
            )}
            <div className="hidden mt-2 print:block ">
              {isImageUrl(
                config?.cdnURL + "public/" + formData.company_license_front_page
              ) && (
                  <img
                    src={
                      config?.cdnURL +
                      "public/" +
                      formData.company_license_front_page
                    }
                    alt={
                      config?.cdnURL +
                      "public/" +
                      formData.company_license_front_page
                    }
                    className="h-[200px] w-[200px] object-contain"
                  />
                )}
            </div>
          </div>
          <div className="mt-4 mb-4 w-1/4">
            <h1 className="text-sm font-semibold text-black">
              Company License Back Page
            </h1>
            {formData.company_license_back_page ? (
              <div className="w-full flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-outline mt-2 rounded-[8px] text-body1 ">
                <p className="more-lines">
                  {formData.company_license_back_page.split("/").pop()}
                </p>
                {hasCompanyPrivilege && <Image
                  src="file-eye"
                  className="cursor-pointer "
                  objectFit={"contain"}
                  onClick={() => {
                    const fileUrl =
                      config?.cdnURL +
                      "public/" +
                      formData.company_license_back_page;
                    const link = document.createElement("a");
                    link.href = fileUrl;
                    link.target = "_blank";
                    link.rel = "noopener noreferrer";
                    link.click();
                  }}
                />}
              </div>
            ) : (
              <p className="mt-1 font-medium text-gray-900">-</p>
            )}
            <div className="hidden mt-2 print:block ">
              {isImageUrl(
                config?.cdnURL + "public/" + formData.company_license_back_page
              ) && (
                  <img
                    src={
                      config?.cdnURL +
                      "public/" +
                      formData.company_license_back_page
                    }
                    alt={
                      config?.cdnURL +
                      "public/" +
                      formData.company_license_back_page
                    }
                    className="h-[200px] w-[200px] object-contain"
                  />
                )}
            </div>
          </div>
        </div>
        <S3ImageUpload
          heading=""
          label="Employee ID Card Front Page"
          disabled={false}
          upload_id="employee_id_card_front_page"
          UploadPath="uploads/employee_id_card_front_page"
          handleUpload={handleFileUpload}
          uploadedFile={formData.employee_id_card_front_page}
          errorMessage={error.employee_id_card_front_page}
          required={true}
          id="employee_id_card_front_page"
          index={1}
          paddingTop={'pt-4'}
        />
        {termsAndConditions && (
          <CustomersTermsAndConditions
            isOpen={termsAndConditions}
            onClose={() => {
              setTermsAndConditions(false);
              dispatch(clearSignaturePath());
            }}
            secondaryButtonText="Decline"
            primaryButtonText="Accept"
            maxWidth="max-w-[461px]"
            buttonWidth="w-[85px]"
            buttonLoading={acceptButtonLoading}
            handleSubmitButtonClick={handleTermsAndConditions}
          />
        )}
      </div>
    );
  }
);

CorporateDetails.displayName = 'CorporateDetails';

export default CorporateDetails